import Modal from './Modal'
import { type MasterProduct, ProductCategoryLabels } from '@esf/shared'

interface DppTemplateDetailModalProps {
  isOpen: boolean
  onClose: () => void
  template: MasterProduct | null
  onEdit: (template: MasterProduct) => void
  onDelete: (template: MasterProduct) => void
  assetCount?: number
}

export default function DppTemplateDetailModal({
  isOpen,
  onClose,
  template,
  onEdit,
  onDelete,
  assetCount = 0,
}: DppTemplateDetailModalProps) {
  if (!template) return null

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase">{label}</label>
      <p className="mt-1">{value || '-'}</p>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="DPP Template Details" maxWidth="2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-500 font-mono mt-1">{template.id}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`badge ${template.isActive ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
              {template.isActive ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-gray-500">Version {template.version}</span>
          </div>
        </div>

        {/* Classification */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
          <InfoRow
            label="Category"
            value={
              <span className="badge bg-esf-navy/10 text-esf-navy">
                {ProductCategoryLabels[template.category as keyof typeof ProductCategoryLabels] || template.category}
              </span>
            }
          />
          <InfoRow label="Subcategory" value={template.subcategory} />
          <InfoRow label="Model" value={template.model} />
          <InfoRow label="SKU" value={template.sku} />
        </div>

        {/* Manufacturer Info */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Manufacturer Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Manufacturer" value={template.manufacturer} />
            <InfoRow label="Country of Origin" value={template.manufacturerCountry} />
            <InfoRow label="GTIN" value={template.gtin} />
          </div>
        </div>

        {/* Materials & Certifications */}
        {((template.materials && template.materials.length > 0) ||
          (template.certifications && template.certifications.length > 0)) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {template.materials && template.materials.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Materials</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {template.materials.map((m, i) => (
                    <span key={i} className="badge bg-blue-50 text-blue-700">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {template.certifications && template.certifications.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Certifications</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {template.certifications.map((c, i) => (
                    <span key={i} className="badge bg-green-50 text-green-700">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sustainability Metrics */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Sustainability Metrics</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-esf-navy">
                {template.carbonFootprint !== undefined ? `${template.carbonFootprint}` : '-'}
              </div>
              <div className="text-xs text-gray-500">kg CO2e</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {template.recyclabilityScore !== undefined ? `${template.recyclabilityScore}%` : '-'}
              </div>
              <div className="text-xs text-gray-500">Recyclability</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {template.expectedLifespan !== undefined ? `${template.expectedLifespan}` : '-'}
              </div>
              <div className="text-xs text-gray-500">Years Lifespan</div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                {template.repairabilityScore !== undefined ? `${template.repairabilityScore}/10` : '-'}
              </div>
              <div className="text-xs text-gray-500">Repairability</div>
            </div>
          </div>
        </div>

        {/* Battery Info (if applicable) */}
        {template.hasBattery && (
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100">
            <h4 className="font-medium text-yellow-800 mb-2">Battery Information</h4>
            <p className="text-sm text-yellow-700">
              This product contains a battery
              {template.batteryCapacityKwh !== undefined && (
                <> with capacity: <strong>{template.batteryCapacityKwh} kWh</strong></>
              )}
            </p>
          </div>
        )}

        {/* Description */}
        {template.description && (
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
            <p className="mt-2 text-gray-700 whitespace-pre-wrap">{template.description}</p>
          </div>
        )}

        {/* Assets using this template */}
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-blue-800">Assets Using This Template</h4>
              <p className="text-sm text-blue-600 mt-1">
                {assetCount} asset{assetCount !== 1 ? 's' : ''} created from this DPP template
              </p>
            </div>
            <div className="text-3xl font-bold text-blue-700">{assetCount}</div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-xs text-gray-500 flex justify-between pt-4 border-t">
          <span>Created: {new Date(template.createdAt).toLocaleString()}</span>
          <span>Updated: {new Date(template.updatedAt).toLocaleString()}</span>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <button
            className="btn btn-outline bg-error-50 text-error-600 hover:bg-error-100 border-error-200"
            onClick={() => onDelete(template)}
          >
            Delete Template
          </button>
          <div className="flex gap-2">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            <button className="btn btn-primary" onClick={() => onEdit(template)}>
              Edit Template
            </button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
